import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateDataSubscriptionData } from './types';

type DataSubscriptionResponse = {
  code: number;
  id: UUID;
  message: string;
};

/**
 * Creates a data subscription.
 *
 * @param {MutateDataSubscriptionData} dataSubscriptionData - An object containing the data subscription details to be created.
 * @returns {Promise<DataSubscriptionResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/create_data_subscription_data_subscription_post
 */
export const createDataSubscription = async (
  dataSubscriptionData: MutateDataSubscriptionData,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_SUBSCRIPTION.CREATE_DATA_SUBSCRIPTION(), {
      json: dataSubscriptionData,
    })
    .json<DataSubscriptionResponse>();

  return response;
};

/**
 * Custom hook for creating a data subscription using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateDataSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dataSubscriptionData: MutateDataSubscriptionData) =>
      createDataSubscription(dataSubscriptionData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllDataSubscription(),
      });
    },
  });
};
